/**
 * ReservationsMobileHeader
 * Mobile-only sticky row for Reservations page: period label (inline expand) + search icon.
 * Period expands to show preset chips; search icon opens the search/sort sheet (handled by parent).
 */

const PRESET_CHIPS = [
  { id: 'current-month', label: 'Current Month' },
  { id: 'previous-month', label: 'Previous Month' },
  { id: 'past-3-months', label: 'Last 3 Months' },
  { id: 'past-6-months', label: 'Last 6 Months' },
  { id: 'past-12-months', label: 'Last 12 Months' },
  { id: 'next-3-months', label: 'Next 3 Months' },
  { id: 'next-6-months', label: 'Next 6 Months' },
  { id: 'this-year', label: 'This Year (Jan - Dec)' },
  { id: 'next-year', label: 'Next Year' },
  { id: 'last-year', label: 'Last Year' },
];

export default function ReservationsMobileHeader({
  periodLabel,
  periodExpanded,
  onPeriodClick,
  onSearchIconClick,
  onSelectPreset,
}) {
  return (
    <div className="reservations-mobile-header" style={styles.wrapper}>
      <div style={styles.row}>
        <button
          type="button"
          onClick={onPeriodClick}
          style={styles.periodButton}
          aria-expanded={periodExpanded}
          aria-label="Change reporting period"
        >
          <span style={styles.periodLabel}>{periodLabel}</span>
          <span style={styles.periodCaret}>{periodExpanded ? '▼' : '▶'}</span>
        </button>
        <button
          type="button"
          onClick={onSearchIconClick}
          style={styles.searchIconButton}
          aria-label="Search and sort"
        >
          🔍
        </button>
      </div>
      {periodExpanded && (
        <div style={styles.chipsRow}>
          {PRESET_CHIPS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              style={styles.chip}
              onClick={() => onSelectPreset?.(id)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: {
    position: 'sticky',
    top: 'calc(var(--owner-top-nav-height, 52px) + 44px)',
    zIndex: 100,
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    borderBottom: '1px solid var(--color-border-light, #e5e7eb)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    boxSizing: 'border-box',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--spacing-2)',
    minHeight: '44px',
  },
  periodButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-1)',
    padding: 'var(--spacing-2) 0',
    background: 'none',
    border: 'none',
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    flex: '1 1 auto',
    minWidth: 0,
    textAlign: 'left',
  },
  periodLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  periodCaret: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    flexShrink: 0,
  },
  searchIconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    padding: 0,
    background: 'none',
    border: 'none',
    fontSize: '1.25rem',
    cursor: 'pointer',
    flexShrink: 0,
    borderRadius: 'var(--radius-md)',
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--spacing-2)',
    paddingTop: 'var(--spacing-2)',
    paddingBottom: 'var(--spacing-1)',
  },
  chip: {
    padding: 'var(--spacing-1) var(--spacing-2)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-brand-primary, #0f4c75)',
    backgroundColor: 'rgba(15, 76, 117, 0.08)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};
